import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarDays, ClipboardPen, ListChecks, Package, Scale, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { dkMessage } from "./dkUtils";
import { DaySheet } from "./DaySheet";
import { MonthGrid } from "./MonthGrid";
import { VarianceBoard } from "./VarianceBoard";
import { Reconcile } from "./Reconcile";
import { ItemsAdmin } from "./ItemsAdmin";
import { SetupAdmin } from "./SetupAdmin";

const C = OPS.dk;

const triggerClass =
    "gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-[color:var(--ink-muted)] transition-[color,background-color] duration-150 ease-out hover:text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)] data-[state=active]:bg-[color:var(--brand-orange)] data-[state=active]:text-white data-[state=active]:shadow-[var(--shadow-sm)]";

// Only built tabs exist: an unknown tab key from the server is never rendered.
// `located` tabs need a location in scope; the reviewer's admin tabs do not, so
// a fresh install with no location yet still reaches items / setup.
const TAB_DEFS = {
    day: { icon: ClipboardPen, label: "dk_tab_day", located: true,
           render: (s) => <DaySheet scope={s.scope} /> },
    month: { icon: CalendarDays, label: "dk_tab_month", located: true,
             render: (s) => <MonthGrid scope={s.scope} canOpenDay={s.tabs.includes("day")} /> },
    variance: { icon: Scale, label: "dk_tab_variance", located: true,
                render: (s) => <VarianceBoard scope={s.scope} canOpenMonth={s.tabs.includes("month")} /> },
    reconcile: { icon: ListChecks, label: "dk_tab_reconcile", located: true,
                 render: (s) => <Reconcile scope={s.scope} /> },
    items: { icon: Package, label: "dk_tab_items", located: false,
             render: (s) => <ItemsAdmin scope={s.scope} /> },
    setup: { icon: SlidersHorizontal, label: "dk_tab_setup", located: false,
             render: (s) => <SetupAdmin scope={s.scope} onChanged={s.reload} /> },
};

/** The server's message for a scope without locations (`cause`, as sc), else ours. */
function noLocationText(scope, lang, t) {
    const d = scope?.cause && typeof scope.cause === "object" ? scope.cause : null;
    const msg = d ? (lang === "ar" ? d.message_ar || d.message_en : d.message_en || d.message_ar) : null;
    return msg || t("dk_no_location");
}

/**
 * «الجرد اليومي» inside the branch hub. The tab LIST is the server's
 * (`section.tabs` from `/branch-ops/scope`); locations, caps, today, the
 * counter's edit window, settings and labels come from ONE `GET /dk/scope`,
 * passed down as `scope`. An empty location scope is a note with the server's
 * own message, never a form.
 */
export function DailyCountSection({ section }) {
    const { t, lang, dir } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const [scope, setScope] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(null);
        api.get("/dk/scope")
            .then((r) => { if (alive) setScope(r.data); })
            .catch((e) => { if (alive) setError(e); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [attempt]);

    // Settings edits change the scope (edit window, locations): refetch it in
    // place, without the loader, so the open tab keeps its state.
    const reloadScope = useCallback(async () => {
        try {
            const r = await api.get("/dk/scope");
            setScope(r.data);
            setError(null);
        } catch {
            /* keep the scope we have; the admin screen reports its own errors */
        }
    }, []);

    const tabs = (section?.tabs || []).filter((k) => TAB_DEFS[k]);
    const requested = searchParams.get("tab");
    const active = tabs.includes(requested) ? requested
        : (tabs.includes(section?.default_tab) ? section.default_tab : tabs[0]);

    const onTabChange = (val) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", val);
        setSearchParams(next, { replace: true });
    };

    if (loading) return <InlineLoader label={t("dk_loading")} />;

    let note = null;
    // no response at all (weak signal): the note offers a retry instead of a page reload
    const offline = !!error && !dkMessage(error, lang);
    if (error) note = dkMessage(error, lang) || t("dk_not_ready");
    else if (scope?.state !== "ok") note = noLocationText(scope, lang, t);

    const canRender = (k) => !error && !!scope && (scope.state === "ok" || !TAB_DEFS[k].located);
    const ctx = { scope, tabs, reload: reloadScope };

    return (
        <div data-testid={C.section} data-state={scope?.state || "error"} className="space-y-5">
            {note ? (
                <div data-testid={C.note}
                     className="flex items-start gap-2 rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-4 py-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--warning-soft-fg)]" />
                    <p className="min-w-0 flex-1 text-sm font-semibold text-[color:var(--ink-muted)]">{note}</p>
                    {offline ? (
                        <Button size="sm" variant="outline" data-testid={C.retry} className="shrink-0"
                                onClick={() => setAttempt((n) => n + 1)}>
                            {t("dk_retry")}
                        </Button>
                    ) : null}
                </div>
            ) : null}
            {/* Radix Tabs stamps dir="ltr" on its root unless told otherwise (there is no
                DirectionProvider), which would lay every daily-count screen out left-to-right. */}
            {tabs.length && !error ? (
                <Tabs value={active} onValueChange={onTabChange} dir={dir} className="space-y-5">
                    <TabsList className="inline-flex h-auto w-full flex-wrap justify-start gap-1 rounded-[1.75rem] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1 sm:w-auto sm:rounded-full">
                        {tabs.map((k) => {
                            const Icon = TAB_DEFS[k].icon;
                            return (
                                <TabsTrigger key={k} data-testid={C.tab(k)} value={k} className={triggerClass}>
                                    <Icon className="h-4 w-4" />
                                    {t(TAB_DEFS[k].label)}
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>
                    {tabs.map((k) => (
                        <TabsContent key={k} value={k} className="mt-0 focus-visible:outline-none">
                            {canRender(k) ? TAB_DEFS[k].render(ctx) : null}
                        </TabsContent>
                    ))}
                </Tabs>
            ) : null}
        </div>
    );
}
